import plantillaGenerica from "./generica";
import scotiabank from "./scotiabank";
import banregio from "./banregio";
import banorte from "./banorte";
import bbva from "./bbva";
import banbajio from "./banbajio";
import santander from "./santander";

/**
 * Registro de plantillas de bancos.
 *
 * Para agregar un banco nuevo:
 *   1. Crear un archivo (ej. ./bbva.js)
 *   2. agregarlo a PLANTILLAS.
 *   3. ponerle su `clavesSat` (clave del catálogo c_Banco del SAT) para que la
 *      plantilla quede ligada al banco configurado en la cuenta.
 *
 * Forma de un movimiento devuelto por parsear():
 *   { fecha: moment, monto: number, tipo: 'cargo' | 'abono' | null, concepto: string, raw: string }
 * Opcional `reversos: { montoNegativo: RegExp, rastreo: RegExp }` para detectar
 * devoluciones del banco (retiro con signo negativo) y ligarlas con su retiro
 * original por clave de rastreo (ver detectarReversos en helpers.js).
 */
export const PLANTILLAS = [
  banregio,
  scotiabank,
  banorte,
  bbva,
  banbajio,
  santander,
];

export { default as plantillaGenerica } from "./generica";

/**
 * Lista para poblar el dropdown de override manual en la UI.
 * Incluye la opción genérica al final.
 */
export function listaPlantillas() {
  return [...PLANTILLAS, plantillaGenerica].map((p) => ({
    id: p.id,
    nombre: p.nombre,
  }));
}

/**
 * Normaliza una clave de banco del SAT a 3 dígitos para poder compararla.
 *
 * El catálogo c_Banco usa 3 dígitos ("012" = BBVA), pero la misma clave puede
 * venir con el prefijo "40" de la CLABE interbancaria ("40012") o sin los ceros
 * a la izquierda ("12"). Los tres casos deben apuntar al mismo banco.
 */
export function normalizarClaveSat(clave) {
  if (clave === null || clave === undefined) return null;
  const digitos = String(clave).replace(/\D/g, "");
  if (!digitos) return null;
  const sinPrefijo =
    digitos.length === 5 && digitos.startsWith("40") ? digitos.slice(2) : digitos;
  return sinPrefijo.padStart(3, "0").slice(-3);
}

/**
 * Plantilla ligada a una clave de banco del SAT. null si ese banco todavía no
 * tiene plantilla propia (entonces se cae a la detección por texto).
 */
export function plantillaPorClaveSat(clave) {
  const buscada = normalizarClaveSat(clave);
  if (!buscada) return null;
  return (
    PLANTILLAS.find((p) =>
      (p.clavesSat || []).some((c) => normalizarClaveSat(c) === buscada)
    ) || null
  );
}

/**
 * Selección de plantilla. La cuenta manda, el PDF sólo confirma:
 *
 *  1. Si se fuerza un id (override manual del usuario), se usa esa.
 *  2. Si la cuenta tiene clave SAT con plantilla propia, se usa ESA plantilla.
 *     Si además el texto del PDF delata otro banco, se devuelve `conflicto`
 *     para que la UI avise y no lea un estado de cuenta ajeno a la cuenta.
 *  3. Sin clave SAT (cuenta sin banco SAT configurado), se auto-detecta leyendo
 *     el texto del PDF, como antes.
 *  4. Si nada matchea, cae a la plantilla genérica.
 *
 * Devuelve { plantilla, autodetectada, porClaveSat, conflicto }:
 *   - autodetectada: se reconoció el banco por el texto del PDF.
 *   - porClaveSat:   se eligió por el banco configurado en la cuenta.
 *   - conflicto:     { esperada, detectada } cuando el PDF es de otro banco.
 */
export function seleccionarPlantilla(texto, idForzado, claveSatCuenta) {
  if (idForzado) {
    const forzada = [...PLANTILLAS, plantillaGenerica].find(
      (p) => p.id === idForzado
    );
    if (forzada) {
      return { plantilla: forzada, autodetectada: false, porClaveSat: false, conflicto: null };
    }
  }

  const claveCuenta = normalizarClaveSat(claveSatCuenta);
  const detectada = PLANTILLAS.find((p) => p.detectar && p.detectar(texto)) || null;
  const deLaCuenta = plantillaPorClaveSat(claveCuenta);

  // El texto del PDF delata un banco cuya clave SAT no es la de la cuenta: el
  // estado de cuenta es de otro banco. Se detecta incluso si el banco de la
  // cuenta todavía no tiene plantilla propia (basta comparar las claves).
  const esDeOtroBanco =
    !!claveCuenta &&
    !!detectada &&
    (detectada.clavesSat || []).length > 0 &&
    !(detectada.clavesSat || []).some((c) => normalizarClaveSat(c) === claveCuenta);

  if (esDeOtroBanco) {
    return {
      plantilla: deLaCuenta || plantillaGenerica,
      autodetectada: false,
      porClaveSat: !!deLaCuenta,
      conflicto: { esperada: deLaCuenta, detectada, claveCuenta },
    };
  }

  if (deLaCuenta) {
    return { plantilla: deLaCuenta, autodetectada: false, porClaveSat: true, conflicto: null };
  }

  if (detectada) {
    return { plantilla: detectada, autodetectada: true, porClaveSat: false, conflicto: null };
  }

  return { plantilla: plantillaGenerica, autodetectada: false, porClaveSat: false, conflicto: null };
}
